import { PsychologistHeadlineEnum } from "@mindlercare/schema";
import { theme as defaultTheme, Select } from "@mindlercare/ui-web";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import useMarketStrategy from "strategies/market/useMarketStrategy";
import { styled } from "styled-components";
import { useApplicationMarket } from "../../../core/selectors/marketSelectors";

type DropdownValue = {
  label: string;
  value: PsychologistHeadlineEnum;
};

type Props = {
  onKeyChange: (key: PsychologistHeadlineEnum) => void;
  selectedKey?: PsychologistHeadlineEnum;
  availableLanguages?: {
    codeISO2: string;
  }[];
};

const HeadlineDropdown = ({ onKeyChange, selectedKey }: Props) => {
  const strategy = useMarketStrategy();
  const { t } = useTranslation();
  const { languages: availableLanguages } = useApplicationMarket();

  const getTextFormatting = useCallback(
    (key: PsychologistHeadlineEnum) =>
      availableLanguages
        ?.map((lang) => t(key, { lng: lang.codeISO2 }))
        .join(" / ") || "",
    [availableLanguages, t],
  );

  const [selectedTitle, setSelectedTitle] = useState<DropdownValue | null>(
    selectedKey
      ? {
          label: getTextFormatting(selectedKey),
          value: selectedKey,
        }
      : null,
  );

  const onChange = (prop: DropdownValue) => {
    onKeyChange(prop.value);
    setSelectedTitle(prop);
  };

  const getFormattedKeys = useCallback((): DropdownValue[] => {
    const formattedKeys = strategy.psychologist.headlineKeys.map((key) => ({
      label: getTextFormatting(key),
      value: key,
    }));
    return formattedKeys;
  }, [getTextFormatting, strategy.psychologist.headlineKeys]);

  const isSingleItem = strategy.psychologist.headlineKeys.length === 1;

  return (
    <HeadlineDropdownContainer>
      {isSingleItem ? (
        <SingleItemText>
          {getTextFormatting(strategy.psychologist.headlineKeys[0])}
        </SingleItemText>
      ) : (
        <Select
          id="psychologist-headline-dropdown"
          label={`${t("PSYCHOLOGIST_TITLE")}${isSingleItem ? "" : "*"}`}
          value={selectedTitle?.value}
        >
          <Select.Trigger placeholder={t("HEADLINE_PLACEHOLDER")}>
            {selectedTitle?.label}
          </Select.Trigger>
          <Select.Options>
            {getFormattedKeys().map((option) => (
              <Select.Option
                key={option.value}
                value={option.value}
                onClick={() => onChange(option)}
              >
                {option.label}
              </Select.Option>
            ))}
          </Select.Options>
        </Select>
      )}
    </HeadlineDropdownContainer>
  );
};

const SingleItemText = styled.p`
  font-size: ${({ theme }) => defaultTheme.FONT.SIZE.STANDARD};
  margin: 0;
`;

const HeadlineDropdownContainer = styled.div`
  margin-top: 1rem;
`;

export default HeadlineDropdown;
